
// Import necessary dependencies
import { supabase } from '@/integrations/supabase/client';
import { Product } from '@/lib/types';
import { transformRemoteToProduct } from './transformers';
import { PRODUCTS_TABLE, getUserId } from './api-utils';

// Primary API function for fetching products
export const fetchProductsFromApi = async (forceRefresh = false): Promise<Product[]> => {
  try {
    console.log('Fetching products from API - forceRefresh:', forceRefresh);
    
    const userId = await getUserId();
    
    const { data: productsData, error: productsError } = await supabase
      .from(PRODUCTS_TABLE)
      .select('*')
      .eq('user_id', userId);
    
    if (productsError) {
      console.error('Error fetching products:', productsError);
      throw productsError;
    }
    
    // Fetch product-group relationships separately to populate groupIds
    const products = productsData || [];
    console.log(`Fetched ${products.length} products from API for user ${userId}`);
    
    if (products.length > 0) {
      const productIds = products.map(p => p.id);
      const { data: groupRelations, error: groupsError } = await supabase
        .from('product_groups')
        .select('*')
        .in('product_id', productIds);
      
      if (groupsError) {
        console.error('Error fetching product-group relationships:', groupsError);
        // Continue anyway, just log the error
      }
      
      // Create a map of product id to group ids
      const productGroupMap = new Map();
      (groupRelations || []).forEach(relation => {
        if (!productGroupMap.has(relation.product_id)) {
          productGroupMap.set(relation.product_id, []);
        }
        productGroupMap.get(relation.product_id).push(relation.group_id);
      });
      
      // Transform products with their group ids
      return products.map(product => {
        const transformedProduct = transformRemoteToProduct(product);
        transformedProduct.groupIds = productGroupMap.get(product.id) || [];
        return transformedProduct;
      });
    }
    
    return products.map(transformRemoteToProduct);
  } catch (error) {
    console.error('Error in fetchProductsFromApi:', error);
    throw error;
  }
};

// Define the fetchProducts function here as an alias to prevent circular dependencies
export const fetchProducts = fetchProductsFromApi;
